import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearWorkHistory,
    loadEmploymentTypeDetails,
    loadKeySoftSkillsDetails,
    loadReasonForLeavingDetails
} from '../../../store/directusService';
import {
    loadParticipantWorkHistory,
    updateParticipantWorkHistory
} from '../../../store/participantService';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    EMPLOYMENT_TYPE_DETAILS_ERROR,
    KEY_SKILLS_ERROR,
    REASON_FOR_LEAVING_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { sortByEndDate } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import LoadingSpinner from '../../ui/LoadingSpinner';

import EditWorkHistory from './EditWorkHistory';
import { validate } from './validateWorkHistory';
import WorkHistoryCard from './WorkHistoryCard';

import app from '../../../app.module.css';
import cards from '../../../commonStyles/cardContainer.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './workHistory.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
const editOnClosedRoles = [SUPERUSER, QUALITY];
const _MESSAGE = 'Participant work history details have been updated';

const WorkHistory = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        participantId: '',
        keySoftSkillIds: [],
        employment: []
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateEntry, setInitialStateEntry] = useState(initialState);
    const [currentEmployment, setCurrentEmployment] = useState({});
    const [editType, setEditType] = useState('create');
    const [isOpenEditor, setIsOpenEditor] = useState(false);
    const [isDisabled, setIsDisabled] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [errors, setErrors] = useState({});

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const keySoftSkillsDetails = useSelector(
        (state) => state.entities.directusService.keySoftSkillsDetails
    );
    const employmentTypeDetails = useSelector(
        (state) => state.entities.directusService.employmentTypeDetails
    );
    const reasonForLeavingDetails = useSelector(
        (state) => state.entities.directusService.reasonForLeavingDetails
    );
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const currentParticipantWorkHistory = useSelector(
        (state) => state.entities.participantService.currentParticipantWorkHistory
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        keySoftSkillsDetails?.length < 1 && dispatch(loadKeySoftSkillsDetails());
        employmentTypeDetails?.length < 1 && dispatch(loadEmploymentTypeDetails());
        reasonForLeavingDetails?.length < 1 && dispatch(loadReasonForLeavingDetails());
        return () => clearScreen();
    }, []);

    useEffect(() => {
        if ('status' in currentParticipant) {
            const disableOnClosedStatus =
                currentParticipant.status === 'Closed' && !hasRole(editOnClosedRoles, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
        }
    }, [currentParticipant?.status, roles]);

    useEffect(() => {
        if ('id' in currentParticipant) dispatch(loadParticipantWorkHistory(currentParticipant.id));
    }, [currentParticipant?.id]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (Object.keys(currentParticipantWorkHistory)?.length < 1) {
            setNewEntry((prev) => ({ ...prev, participantId: currentParticipant?.id }));
            setInitialStateEntry((prev) => ({ ...prev, participantId: currentParticipant?.id }));
        } else {
            let sortedEmployment;
            sortedEmployment =
                currentParticipantWorkHistory?.employment &&
                sortByEndDate([...currentParticipantWorkHistory.employment]);
            const updatedWorkHistory = {
                ...currentParticipantWorkHistory,
                employment: sortedEmployment || []
            };
            setNewEntry(updatedWorkHistory);
            setInitialStateEntry(currentParticipantWorkHistory);
        }
    }, [currentParticipantWorkHistory]);

    useEffect(() => {
        if (successMessage === _MESSAGE) setIsButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (employmentTypeDetails?.length < 1) return EMPLOYMENT_TYPE_DETAILS_ERROR;
        if (keySoftSkillsDetails?.length < 1) return KEY_SKILLS_ERROR;
        if (reasonForLeavingDetails?.length < 1) return REASON_FOR_LEAVING_ERROR;
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
    };

    const clearScreen = () => {
        setNewEntry(initialState);
        setInitialStateEntry(initialState);
        setErrors({});
        setCurrentEmployment({});
        dispatch(clearWorkHistory());
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false, message: '' } }));
    };

    // EVENT HANDLERS
    const onAddCard = () => {
        setEditType('create');
        setIsOpenEditor(true);
    };

    const onUpdate = (key, value) => {
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onEditEmployment = (employment) => {
        setCurrentEmployment(employment);
        setEditType('edit');
        setIsOpenEditor(true);
    };

    const deleteEmployment = (employment) => {
        setNewEntry((prev) => ({
            ...prev,
            employment: newEntry.employment.filter((el) => el.id !== employment.id)
        }));
    };

    const addEmployment = (newEmployment) => {
        setIsOpenEditor(false);
        setNewEntry((prev) => ({
            ...prev,
            employment: sortByEndDate([...newEntry.employment, newEmployment])
        }));
    };

    const updateEmployment = (employment) => {
        setIsOpenEditor(false);
        setCurrentEmployment({});
        setNewEntry((prev) => ({
            ...prev,
            employment: newEntry.employment.map((el) => (el.id === employment.id ? employment : el))
        }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateEntry);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        const payload = {
            ...newEntry,
            employment: newEntry.employment.map((el) => ({ ...el, id: null }))
        };
        dispatch(updateParticipantWorkHistory(payload, payload.participantId, _MESSAGE));
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div className={form.formWrapper}>
            <form onSubmit={onSubmit} data-testid="work_history_form">
                <h2 className={app.sectionHeading}>Past Employment</h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="keySoftSkillIds"
                            label="Key Skills"
                            placeholder="Select key skills..."
                            disabled={isDisabled}
                            menuItems={keySoftSkillsDetails || []}
                            preSelectedIds={newEntry.keySoftSkillIds || []}
                            preSelects={keySoftSkillsDetails.filter((el) =>
                                newEntry.keySoftSkillIds?.includes(el.id)
                            )}
                            onChange={(chosenIds) => onUpdate('keySoftSkillIds', chosenIds)}
                        />
                    </div>
                </div>
                <div className={cards.cards}>
                    {newEntry.employment.map((el) => (
                        <div key={el.id}>
                            <WorkHistoryCard
                                employment={el}
                                isDisabled={isDisabled}
                                onEditEmployment={onEditEmployment}
                                onDeleteEmployment={deleteEmployment}
                            />
                        </div>
                    ))}
                    {isOpenEditor && (
                        <EditWorkHistory
                            employment={currentEmployment}
                            participant={currentParticipant}
                            employmentTypeDetails={employmentTypeDetails}
                            reasonForLeavingDetails={reasonForLeavingDetails}
                            isDisabled={isDisabled}
                            editType={editType}
                            isOpen={isOpenEditor}
                            onCancel={() => setIsOpenEditor(false)}
                            onUpdate={updateEmployment}
                            onAdd={addEmployment}
                        />
                    )}
                </div>

                <div className={local.actions}>
                    <Button
                        id="addPastEmploymentCard"
                        type="button"
                        onClick={onAddCard}
                        disabled={isDisabled}
                        content="Add Past Employment"
                    />
                    <div>
                        <Button
                            id="workHistorySubmitButton"
                            disabled={isDisabled || isButtonDisabled}
                            content="Update Past Employment"
                            error={errors.button}
                            clearError={() => clearError('button')}
                        />
                    </div>
                </div>
            </form>
        </div>
    );
};

export default WorkHistory;
